import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type RateLimit = {
  key: string;
  description: string;
  kind: "per_user" | "per_ip";
  max_requests: number;
  window_seconds: number;
  updated_at: string | null;
  updated_by_email: string | null;
};

type AuditEntry = {
  id: number;
  key: string;
  old_max: number | null;
  old_window: number | null;
  new_max: number;
  new_window: number;
  changed_at: string;
  changed_by_email: string | null;
};

function isUnusual(max: number, windowSeconds: number): boolean {
  return max < 3 || max > 1000 || windowSeconds < 5 || windowSeconds > 86400;
}

const KIND_TITLE: Record<RateLimit["kind"], string> = {
  per_user: "Per-gebruiker limieten",
  per_ip: "Per-IP limieten (niet-geauthenticeerd)",
};

export function AdminRateLimitsTab() {
  const [items, setItems] = useState<RateLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await authFetchJson<{ items: RateLimit[] }>("/api/admin/rate-limits");
      setItems(res.items ?? []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await authFetchJson<{ items: AuditEntry[] }>("/api/admin/rate-limits/audit?limit=50");
      setHistory(res.items ?? []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const onSaved = useCallback((updated: RateLimit) => {
    setItems((prev) => prev.map((it) => (it.key === updated.key ? updated : it)));
    if (historyOpen) void loadHistory();
  }, [historyOpen, loadHistory]);

  // Group by `kind` returned from the API, preserving the order items
  // arrive in (which mirrors dependencies.py declaration order).
  const groups = useMemo(() => {
    const buckets = new Map<RateLimit["kind"], RateLimit[]>();
    for (const item of items) {
      const bucket = buckets.get(item.kind) ?? [];
      bucket.push(item);
      buckets.set(item.kind, bucket);
    }
    return Array.from(buckets.entries());
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--cs-ink-muted)]">
          Wijzigingen worden direct toegepast op deze pod en binnen 30 seconden op alle andere pods.
        </p>
        <button
          type="button"
          onClick={() => {
            const next = !historyOpen;
            setHistoryOpen(next);
            if (next) void loadHistory();
          }}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
        >
          {historyOpen ? "Geschiedenis verbergen" : "Geschiedenis ▾"}
        </button>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {loadError}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : (
        groups.map(([kind, kindItems]) => (
          <Section key={kind} title={KIND_TITLE[kind]} items={kindItems} onSaved={onSaved} />
        ))
      )}

      {historyOpen ? (
        <HistoryDrawer entries={history} loading={historyLoading} />
      ) : null}
    </div>
  );
}

function Section({ title, items, onSaved }: { title: string; items: RateLimit[]; onSaved: (r: RateLimit) => void }) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">{title}</h3>
      <div className="overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cs-ivory)] text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
            <tr>
              <th className="px-4 py-2 text-left">Limiet</th>
              <th className="px-4 py-2 text-left">Max requests</th>
              <th className="px-4 py-2 text-left">Per (sec)</th>
              <th className="px-4 py-2 text-left">Laatst gewijzigd</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
            {items.map((item) => (
              <Row key={item.key} item={item} onSaved={onSaved} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({ item, onSaved }: { item: RateLimit; onSaved: (r: RateLimit) => void }) {
  const [maxReq, setMaxReq] = useState(item.max_requests);
  const [windowSeconds, setWindowSeconds] = useState(item.window_seconds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setMaxReq(item.max_requests);
    setWindowSeconds(item.window_seconds);
  }, [item.max_requests, item.window_seconds]);

  const dirty = maxReq !== item.max_requests || windowSeconds !== item.window_seconds;
  const unusual = isUnusual(maxReq, windowSeconds);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await authFetchJson<RateLimit>(`/api/admin/rate-limits/${item.key}`, {
        method: "PATCH",
        body: JSON.stringify({ max_requests: maxReq, window_seconds: windowSeconds }),
      });
      onSaved(updated);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Opslaan mislukt.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr className="text-xs hover:bg-[var(--cs-ivory)]/60">
        <td className="px-4 py-2">
          <p className="font-medium text-[var(--cs-navy)]">{item.description}</p>
          <p className="text-[10px] text-[var(--cs-ink-muted)]">{item.key}</p>
        </td>
        <td className="px-4 py-2">
          <input
            type="number"
            min={1}
            value={maxReq}
            onChange={(e) => setMaxReq(Number(e.target.value))}
            className="w-24 rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1 text-sm tabular-nums"
          />
        </td>
        <td className="px-4 py-2">
          <input
            type="number"
            min={1}
            value={windowSeconds}
            onChange={(e) => setWindowSeconds(Number(e.target.value))}
            className="w-28 rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1 text-sm tabular-nums"
          />
        </td>
        <td className="px-4 py-2 text-[11px] text-[var(--cs-ink-muted)]">
          <p>{formatDateTime(item.updated_at)}</p>
          {item.updated_by_email ? <p>{item.updated_by_email}</p> : null}
        </td>
        <td className="px-4 py-2 text-right">
          {savedFlash ? <span className="mr-2 text-xs text-green-700">✓ opgeslagen</span> : null}
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="rounded-full bg-[var(--cs-rose)] px-4 py-1.5 text-xs font-medium text-white shadow-[0_8px_20px_rgba(225,29,72,0.20)] hover:bg-[var(--cs-rose-dark)] disabled:opacity-40"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </td>
      </tr>
      {unusual || error ? (
        <tr>
          <td colSpan={5} className="px-4 pb-2">
            {unusual ? (
              <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                Ongebruikelijke waarde. Weet u zeker dat dit klopt?
              </p>
            ) : null}
            {error ? (
              <p className="mt-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                {error}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function HistoryDrawer({ entries, loading }: { entries: AuditEntry[]; loading: boolean }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">Geschiedenis</h3>
      <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
        <table className="w-full text-xs">
          <thead className="bg-[var(--cs-ivory)] text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
            <tr>
              <th className="px-4 py-2 text-left">Wanneer</th>
              <th className="px-4 py-2 text-left">Wie</th>
              <th className="px-4 py-2 text-left">Limiet</th>
              <th className="px-4 py-2 text-left">Oud</th>
              <th className="px-4 py-2 text-left">Nieuw</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-3 text-[var(--cs-ink-muted)]">Laden…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-3 text-[var(--cs-ink-muted)]">Geen wijzigingen.</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-2">{formatDateTime(e.changed_at)}</td>
                <td className="px-4 py-2">{e.changed_by_email ?? "—"}</td>
                <td className="px-4 py-2">{e.key}</td>
                <td className="px-4 py-2 tabular-nums">{e.old_max ?? "—"} / {e.old_window ?? "—"}s</td>
                <td className="px-4 py-2 tabular-nums">{e.new_max} / {e.new_window}s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
